import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { MessageSquareDot, Bug, MessageSquare, Lightbulb, Check, X } from 'lucide-react'
import { api } from '@/services/api'
import { cn } from '@/lib/cn'

// 'feature' exists so a feature request is TYPED at the point of capture. It
// used to arrive as general feedback and be re-read as a bug or not at all; the
// Trello ingest routes on this value (bug → Bugs list, feature → Planning), and
// no downstream reader can recover the kind from prose.
type FeedbackType = 'feedback' | 'bug' | 'feature'
type Priority = 'low' | 'normal' | 'high'

/**
 * Floating "Feedback" affordance — a fixed bottom-right pill on every routed
 * page. Clicking it opens a modal form that can file general feedback, a
 * feature request, or a structured bug report:
 *
 *   - feedback → a headline + free-text comment + thumbs rating
 *   - feature  → a headline + free-text description (no rating: "is this page
 *                helpful" is not the question a feature request answers)
 *   - bug      → a headline + priority + steps / expected / actual repro fields
 *
 * Submissions POST /feedback. Admins read + triage everything at
 * /admin/feedback.
 */
/** Controlled by AppShell, because the TRIGGER lives in two places while the
 *  panel lives here.
 *
 *  Below lg the trigger is a button in the mobile header; at lg+ it is the
 *  floating pill in the bottom-right. The float used to render at every width,
 *  and being position:fixed z-40 it painted over whatever occupied the
 *  viewport's bottom-right corner — on a phone that is row actions. Measured on
 *  deployed testing at 390px: "Convert" on /prospects sat 1px from its edge,
 *  and elements on /automations and /admin/permissions sat 0px from it.
 *
 *  The panel itself needed no change: it was already a centred full-screen
 *  modal (fixed inset-0 z-50), which is the right shape on a phone. */
export function PageFeedback({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { pathname } = useLocation()
  // Alias so the existing call sites below read unchanged.
  const setOpen = onOpenChange
  const [type, setType] = useState<FeedbackType>('feedback')
  const [helpful, setHelpful] = useState<boolean | null>(null)
  const [priority, setPriority] = useState<Priority>('normal')
  const [title, setTitle] = useState('')
  const [comment, setComment] = useState('')
  const [steps, setSteps] = useState('')
  const [expected, setExpected] = useState('')
  const [actual, setActual] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)

  function resetForm() {
    setType('feedback')
    setHelpful(null)
    setPriority('normal')
    setTitle('')
    setComment('')
    setSteps('')
    setExpected('')
    setActual('')
    setSent(false)
  }

  // Reset the flow whenever the user navigates to a new page so a "sent" state
  // (or a half-filled bug report) from one page doesn't bleed onto the next.
  useEffect(() => {
    setOpen(false)
    resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const isBug = type === 'bug'
  const isFeature = type === 'feature'
  // Feedback needs a comment or a rating; a bug needs a title and an actual
  // result. A feature request needs a title or a description — the thumbs
  // rating is not an answer to "what should we build", so unlike feedback it
  // cannot stand alone as the whole submission.
  const canSend = isBug
    ? title.trim().length > 0 && actual.trim().length > 0
    : isFeature
      ? title.trim().length > 0 || comment.trim().length > 0
      : comment.trim().length > 0 || helpful !== null

  async function send() {
    if (!canSend) return
    const body = {
      page: pathname,
      type,
      // Backend requires `helpful` as a boolean. For a bug report there's no
      // thumbs control, so send false (a bug is not a "helpful page" signal);
      // for feedback, a missing rating reads as engaged → true. A feature
      // request has no thumbs control either, but it is not a complaint about
      // the page — it reads as engaged, same as unrated feedback.
      // Feature is set explicitly so a thumbs-down picked under Feedback before
      // switching type cannot ride along on a feature request.
      helpful: isBug ? false : isFeature ? true : (helpful ?? true),
      title: title.trim() || null,
      comment: comment.trim() || null,
      priority: isBug ? priority : null,
      steps: isBug ? steps.trim() || null : null,
      expected: isBug ? expected.trim() || null : null,
      actual: isBug ? actual.trim() || null : null,
    }
    setSending(true)
    try {
      await api.post('/feedback', body)
      setSent(true)
      // Auto-close shortly after a successful send and clear the form so the
      // user can file another on the same page without navigating away.
      setTimeout(() => {
        setOpen(false)
        resetForm()
      }, 1500)
    } catch {
      // Failure just means retry — feedback isn't critical enough for heavy UI.
      setSent(false)
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false)
          }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Send feedback or report a bug"
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-card"
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <div className="text-sm font-semibold text-slate-800">
                {isBug ? 'Report a bug' : isFeature ? 'Request a feature' : 'Send feedback'}
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                title="Close (Esc)"
                aria-label="Close"
              >
                <X size={15} />
              </button>
            </div>

            {sent ? (
              <div className="flex items-center justify-center gap-1.5 py-10 text-sm text-emerald-700">
                <Check size={15} /> Thanks — your{' '}
                {isBug ? 'bug report' : isFeature ? 'feature request' : 'feedback'} was sent.
              </div>
            ) : (
              <div className="max-h-[70vh] overflow-y-auto p-4">
                {/* Type toggle */}
                <div className="mb-3 inline-flex rounded-lg border border-slate-200 p-0.5 text-xs">
                  <button
                    onClick={() => setType('feedback')}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium transition',
                      type === 'feedback' ? 'bg-penser-blue text-white' : 'text-slate-500 hover:text-slate-700',
                    )}
                  >
                    <MessageSquare size={13} /> Feedback
                  </button>
                  <button
                    onClick={() => setType('feature')}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium transition',
                      isFeature ? 'bg-amber-500 text-white' : 'text-slate-500 hover:text-slate-700',
                    )}
                  >
                    <Lightbulb size={13} /> Feature
                  </button>
                  <button
                    onClick={() => setType('bug')}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium transition',
                      isBug ? 'bg-red-600 text-white' : 'text-slate-500 hover:text-slate-700',
                    )}
                  >
                    <Bug size={13} /> Report a bug
                  </button>
                </div>

                <div className="mb-3 text-[11px] text-slate-500">
                  {isBug
                    ? 'Tell us what went wrong. The more repro detail, the faster we can fix it.'
                    : isFeature
                      ? 'What should this page do that it does not do yet? Describe the outcome you want.'
                      : "What's working, missing, or could be better on this page? Admins read every entry."}
                  <span className="ml-1 text-slate-400">Page: </span>
                  <code className="font-mono text-[10px] text-slate-400">{pathname}</code>
                </div>

                {/* Title (headline) */}
                <label className="mb-1 block text-[11px] font-medium text-slate-600">
                  {isBug ? 'Summary' : 'Title'}
                  {isBug && <span className="text-red-500"> *</span>}
                </label>
                <input
                  autoFocus
                  className="penser-input mb-3 w-full text-xs"
                  placeholder={
                    isBug
                      ? 'e.g. Export button does nothing'
                      : isFeature
                        ? 'e.g. Export the pipeline to Excel'
                        : 'Optional one-line summary'
                  }
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />

                {isBug ? (
                  <>
                    <label className="mb-1 block text-[11px] font-medium text-slate-600">Priority</label>
                    <select
                      className="penser-input mb-3 w-full text-xs"
                      value={priority}
                      onChange={(e) => setPriority(e.target.value as Priority)}
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                    </select>

                    <label className="mb-1 block text-[11px] font-medium text-slate-600">Steps to reproduce</label>
                    <textarea
                      className="penser-input mb-3 min-h-[70px] w-full resize-y text-xs"
                      placeholder="1. Go to…&#10;2. Click…&#10;3. …"
                      value={steps}
                      onChange={(e) => setSteps(e.target.value)}
                    />

                    <label className="mb-1 block text-[11px] font-medium text-slate-600">Expected result</label>
                    <textarea
                      className="penser-input mb-3 min-h-[50px] w-full resize-y text-xs"
                      placeholder="What you expected to happen"
                      value={expected}
                      onChange={(e) => setExpected(e.target.value)}
                    />

                    <label className="mb-1 block text-[11px] font-medium text-slate-600">
                      Actual result<span className="text-red-500"> *</span>
                    </label>
                    <textarea
                      className="penser-input mb-1 min-h-[50px] w-full resize-y text-xs"
                      placeholder="What actually happened"
                      value={actual}
                      onChange={(e) => setActual(e.target.value)}
                    />
                  </>
                ) : (
                  <>
                    <label className="mb-1 block text-[11px] font-medium text-slate-600">Details</label>
                    <textarea
                      className="penser-input mb-3 min-h-[110px] w-full resize-y text-xs"
                      placeholder={isFeature ? 'Describe the request…' : 'Type your feedback…'}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault()
                          if (canSend) void send()
                        }
                      }}
                    />
                    <div className={cn('items-center gap-2', isFeature ? 'hidden' : 'flex')}>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400">Rating</span>
                      <button
                        onClick={() => setHelpful((v) => (v === true ? null : true))}
                        title="This page is helpful"
                        className={cn(
                          'rounded px-2 py-1 text-[11px] transition',
                          helpful === true
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'text-slate-400 hover:bg-slate-100 hover:text-emerald-700',
                        )}
                      >
                        Helpful
                      </button>
                      <button
                        onClick={() => setHelpful((v) => (v === false ? null : false))}
                        title="This page needs work"
                        className={cn(
                          'rounded px-2 py-1 text-[11px] transition',
                          helpful === false
                            ? 'bg-red-100 text-red-700'
                            : 'text-slate-400 hover:bg-slate-100 hover:text-red-700',
                        )}
                      >
                        Needs work
                      </button>
                    </div>
                  </>
                )}

                <div className="mt-4 flex items-center justify-end gap-2">
                  <button onClick={() => setOpen(false)} className="penser-button-ghost text-xs">
                    Cancel
                  </button>
                  <button
                    onClick={() => void send()}
                    disabled={sending || !canSend}
                    className="penser-button-primary text-xs"
                    title={isBug ? 'Submit bug report' : 'Cmd/Ctrl + Enter'}
                  >
                    {sending ? 'Sending…' : isBug ? 'Submit bug' : isFeature ? 'Send request' : 'Send'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <button
        onClick={() => {
          if (!open && sent) resetForm()
          setOpen(!open)
        }}
        className={cn(
          // hidden lg:flex — DESKTOP ONLY. Below lg the trigger is in the
          // mobile header (AppShell) so nothing overlays page content.
          'fixed bottom-6 right-6 z-40 hidden lg:flex items-center gap-2 rounded-full border bg-white px-3.5 py-2 text-xs font-medium shadow-card transition',
          open
            ? 'border-penser-blue text-penser-blue-dark'
            : 'border-slate-200 text-slate-600 hover:border-penser-blue/40 hover:text-penser-blue-dark',
        )}
        title="Send feedback or report a bug"
        aria-expanded={open}
      >
        <MessageSquareDot size={13} />
        Feedback
      </button>
    </>
  )
}
